import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { ChevronRight, FileText, Pencil, Trash2, Upload } from "lucide-react";

import styles from "./FormTemplates.module.css";
import { getToken, useMsalAuth } from "../../authConfig";
import {
    createFormTemplateApi,
    deleteFormTemplateApi,
    getFormTemplateApi,
    listFormTemplatesApi,
    parseFormTemplateApi,
    updateFormTemplateApi
} from "../../api/api";
import { FormFieldDef, FormTemplateSchema, FormTemplateSummary } from "../../api/models";
import { AnswerLoading } from "../Answer";
import { validateModel1Template } from "../ReportGenerationFlow/schemaBridge/validateModel1Template";

interface EditorState {
    mode: "create" | "edit";
    templateId?: string;
    name: string;
    jsonText: string;
    sourceFileName: string;
    truncated: boolean;
}

const FieldPreviewList = ({ fields }: { fields: FormFieldDef[] }) => (
    <ul className={styles.fieldList}>
        {fields.map(field => (
            <li key={field.field_name}>
                <div className={styles.fieldRow}>
                    <span className={styles.fieldName}>{field.field_name}</span>
                    <span className={styles.fieldType}>
                        {field.type}
                        {field.type === "table" && field.table_columns
                            ? ` (${field.table_columns.map(column => column.column_name).join(", ")})`
                            : ""}
                    </span>
                </div>
                {field.properties && field.properties.length > 0 && (
                    <ul className={styles.nested}>
                        <FieldPreviewList fields={field.properties} />
                    </ul>
                )}
            </li>
        ))}
    </ul>
);

export const FormTemplateManager = () => {
    const { t } = useTranslation();
    const client = useMsalAuth ? useMsal().instance : undefined;

    const [templates, setTemplates] = useState<FormTemplateSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [editor, setEditor] = useState<EditorState | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const getAuthToken = useCallback(async (): Promise<string | undefined> => {
        try {
            return client ? await getToken(client) : undefined;
        } catch (authError) {
            console.error("Failed to resolve auth token:", authError);
            return undefined;
        }
    }, [client]);

    const refreshTemplates = useCallback(async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            const token = await getAuthToken();
            const response = await listFormTemplatesApi(token);
            setTemplates(response.templates);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
        } finally {
            setLoading(false);
        }
    }, [getAuthToken, t]);

    useEffect(() => {
        void refreshTemplates();
    }, [refreshTemplates]);

    const parsedDraft = useMemo(() => {
        if (!editor) return null;
        try {
            const validated = validateModel1Template(JSON.parse(editor.jsonText));
            if (validated.ok) {
                return { schema: validated.template, errors: [] as string[] };
            }
            return { schema: null, errors: validated.errors };
        } catch (parseError) {
            return { schema: null, errors: [parseError instanceof Error ? parseError.message : "invalid_json"] };
        }
    }, [editor]);

    const handleUploadSelected = useCallback(
        async (file: File | null) => {
            if (!file) return;
            setBusy(true);
            setErrorMessage("");
            try {
                const token = await getAuthToken();
                const response = await parseFormTemplateApi(file, token);
                setEditor({
                    mode: "create",
                    name: response.schema.document_type || file.name.replace(/\.[^.]+$/, ""),
                    jsonText: JSON.stringify(response.schema, null, 2),
                    sourceFileName: response.source_file_name,
                    truncated: response.truncated
                });
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
            } finally {
                setBusy(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        },
        [getAuthToken, t]
    );

    const handleEdit = useCallback(
        async (summary: FormTemplateSummary) => {
            setBusy(true);
            setErrorMessage("");
            try {
                const token = await getAuthToken();
                const template = await getFormTemplateApi(summary.id, token);
                setEditor({
                    mode: "edit",
                    templateId: template.id,
                    name: template.name,
                    jsonText: JSON.stringify(template.schema, null, 2),
                    sourceFileName: template.source_file_name || "",
                    truncated: false
                });
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
            } finally {
                setBusy(false);
            }
        },
        [getAuthToken, t]
    );

    const handleDelete = useCallback(
        async (summary: FormTemplateSummary) => {
            if (!window.confirm(t("forms.deleteConfirm", { name: summary.name }))) return;
            setBusy(true);
            setErrorMessage("");
            try {
                const token = await getAuthToken();
                await deleteFormTemplateApi(summary.id, token);
                await refreshTemplates();
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
            } finally {
                setBusy(false);
            }
        },
        [getAuthToken, refreshTemplates, t]
    );

    const handleSave = useCallback(async () => {
        if (!editor || !parsedDraft?.schema) return;
        const name = editor.name.trim();
        if (!name) {
            setErrorMessage(t("forms.nameRequired"));
            return;
        }
        setBusy(true);
        setErrorMessage("");
        try {
            const token = await getAuthToken();
            const schema = parsedDraft.schema as unknown as FormTemplateSchema;
            if (editor.mode === "create") {
                await createFormTemplateApi({ name, schema, source_file_name: editor.sourceFileName }, token);
            } else if (editor.templateId) {
                await updateFormTemplateApi(editor.templateId, { name, schema }, token);
            }
            setEditor(null);
            await refreshTemplates();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
        } finally {
            setBusy(false);
        }
    }, [editor, parsedDraft, getAuthToken, refreshTemplates, t]);

    const content = loading ? (
        <AnswerLoading />
    ) : (
        <div className={styles.section}>
            {errorMessage && <p className={styles.error}>{errorMessage}</p>}

            {!editor && (
                <>
                    <div className={styles.toolbar}>
                        <button
                            type="button"
                            className={styles.primaryButton}
                            disabled={busy}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Upload size={16} />
                            {busy ? t("forms.parsing") : t("forms.uploadButton")}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            className={styles.hiddenInput}
                            onChange={event => void handleUploadSelected(event.target.files?.[0] || null)}
                        />
                        <span className={styles.hint}>{t("forms.uploadHint")}</span>
                    </div>

                    {templates.length > 0 ? (
                        <div className={styles.list}>
                            {templates.map(summary => (
                                <div key={summary.id} className={styles.listItem}>
                                    <div>
                                        <div className={styles.itemName}>{summary.name}</div>
                                        <div className={styles.itemMeta}>
                                            {summary.source_file_name}
                                            {summary.updated_at ? ` · ${summary.updated_at.slice(0, 10)}` : ""}
                                        </div>
                                    </div>
                                    <div className={styles.itemActions}>
                                        <button
                                            type="button"
                                            className={styles.linkButton}
                                            disabled={busy}
                                            onClick={() => void handleEdit(summary)}
                                        >
                                            <Pencil size={14} />
                                            {t("forms.edit")}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.dangerButton}
                                            disabled={busy}
                                            onClick={() => void handleDelete(summary)}
                                        >
                                            <Trash2 size={14} />
                                            {t("forms.delete")}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.hint}>{t("forms.listEmpty")}</p>
                    )}
                </>
            )}

            {editor && (
                <div className={styles.editorPanel}>
                    <div className={styles.formRow}>
                        <label className={styles.label} htmlFor="forms-template-name">
                            {t("forms.nameLabel")}
                        </label>
                        <input
                            id="forms-template-name"
                            className={styles.textInput}
                            value={editor.name}
                            onChange={event => setEditor({ ...editor, name: event.target.value })}
                        />
                    </div>

                    {editor.truncated && <p className={styles.error}>{t("forms.truncatedWarning")}</p>}

                    {parsedDraft?.schema ? (
                        <div className={styles.formRow}>
                            <span className={styles.label}>{t("forms.fieldsHeading")}</span>
                            <FieldPreviewList fields={parsedDraft.schema.fields as unknown as FormFieldDef[]} />
                        </div>
                    ) : (
                        <p className={styles.error}>
                            {t("forms.schemaInvalid")}
                            {parsedDraft?.errors.length ? ` ${parsedDraft.errors.slice(0, 3).join("; ")}` : ""}
                        </p>
                    )}

                    <details className={styles.detailsJson}>
                        <summary>{t("forms.rawJson")}</summary>
                        <textarea
                            className={styles.jsonTextarea}
                            value={editor.jsonText}
                            onChange={event => setEditor({ ...editor, jsonText: event.target.value })}
                        />
                    </details>

                    <div className={styles.stageActions}>
                        <button
                            type="button"
                            className={styles.primaryButton}
                            disabled={busy || !parsedDraft?.schema}
                            onClick={() => void handleSave()}
                        >
                            {busy ? t("forms.saving") : t("forms.save")}
                        </button>
                        <button type="button" className={styles.linkButton} disabled={busy} onClick={() => setEditor(null)}>
                            {t("forms.cancel")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={styles.settingsSection}>
            <details className={styles.disclosure} open>
                <summary className={styles.summary}>
                    <ChevronRight size={16} />
                    <FileText size={18} />
                    <span className={styles.summaryTitle}>{t("settingsPage.company.formTemplates.title")}</span>
                    <span className={styles.summaryMeta}>
                        {t("settingsPage.company.formTemplates.summary", { count: templates.length })}
                    </span>
                </summary>
                <div className={styles.body}>
                    <p className={styles.description}>{t("settingsPage.company.formTemplates.description")}</p>
                    {content}
                </div>
            </details>
        </div>
    );
};
